import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore, { api } from '../store/useStore';

export default function Login() {
  const { login, fetchSettings } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('login');
  const [activateForm, setActivateForm] = useState({ token: '', password: '', confirm: '' });
  const [tokenUsername, setTokenUsername] = useState('');
  const [tokenLookupState, setTokenLookupState] = useState('');

  const handleLogin = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await login(form.username, form.password);
      await fetchSettings();
      navigate(data.role === 'superadmin' ? '/superadmin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const lookupToken = async (token) => {
    const t = token.trim();
    if (!t) { setTokenUsername(''); setTokenLookupState(''); return; }
    setTokenLookupState('loading');
    try {
      const { data } = await api.get(`/auth/token-info/${t}`);
      setTokenUsername(data.username);
      setTokenLookupState('found');
    } catch {
      setTokenUsername('');
      setTokenLookupState('invalid');
    }
  };

  const handleActivate = async e => {
    e.preventDefault();
    if (activateForm.password !== activateForm.confirm) {
      setError('Passwords do not match');
      return;
    }
    if (activateForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/activate', {
        token: activateForm.token.trim(),
        password: activateForm.password,
      });
      localStorage.setItem('mv_token', data.access_token);
      useStore.setState({ user: { username: data.username, role: data.role } });
      await fetchSettings();
      navigate(data.role === 'superadmin' ? '/superadmin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Activation failed — check your token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">ClassMail Client</div>
        <div className="login-sub">Secure email management platform</div>

        <div className="flex gap-2" style={{ marginBottom: 24 }}>
          <button
            className={`btn btn-sm ${tab === 'login' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setTab('login'); setError(''); }}>
            Sign In
          </button>
          <button
            className={`btn btn-sm ${tab === 'activate' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setTab('activate'); setError(''); }}>
            First-Time Setup
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {tab === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input className="form-input" value={form.username} autoComplete="username"
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" value={form.password} autoComplete="current-password"
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
              type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 14, textAlign: 'center' }}>
              New admin? Click <strong style={{ color: 'var(--text2)', cursor: 'pointer' }}
                onClick={() => setTab('activate')}>First-Time Setup</strong> to activate your account.
            </p>
          </form>
        ) : (
          <form onSubmit={handleActivate}>
            <div style={{ background: 'rgba(88,166,255,.08)', border: '1px solid rgba(88,166,255,.2)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text2)' }}>
              Contact <strong style={{ color: 'var(--text)' }}>ClassToolz admin</strong> to get your activation token and your preferred username. Paste the token below and set your password. Login with the username and your set password on the <strong style={{ color: 'var(--text)', cursor: 'pointer' }} onClick={() => setTab('login')}>Login tab</strong>.
            </div>
            <div className="form-group">
              <label className="form-label">Activation Token</label>
              <input className="form-input" value={activateForm.token}
                onChange={e => { setActivateForm(f => ({ ...f, token: e.target.value })); setTokenUsername(''); setTokenLookupState(''); }}
                onBlur={e => lookupToken(e.target.value)}
                required
                placeholder="Paste token from your super admin" />
              {tokenLookupState === 'found' && (
                <div style={{ marginTop: 6, padding: '6px 10px', background: 'rgba(63,185,80,.1)', border: '1px solid var(--success)', borderRadius: 6, fontSize: 13, color: 'var(--success)' }}>
                  Token valid — you'll log in as <strong>{tokenUsername}</strong>
                </div>
              )}
              {tokenLookupState === 'invalid' && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
                  Token not found or expired — check with your super admin
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Set Your Password</label>
              <input className="form-input" type="password" value={activateForm.password}
                onChange={e => setActivateForm(f => ({ ...f, password: e.target.value }))} required autoComplete="new-password" />
              <div className="form-hint">Minimum 8 characters</div>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" value={activateForm.confirm}
                onChange={e => setActivateForm(f => ({ ...f, confirm: e.target.value }))} required autoComplete="new-password" />
            </div>
            <button className="btn btn-success" style={{ width: '100%', justifyContent: 'center' }}
              type="submit" disabled={loading}>
              {loading ? 'Activating…' : 'Activate & Sign In'}
            </button>
            <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 14, textAlign: 'center' }}>
              Already activated? <strong style={{ color: 'var(--text2)', cursor: 'pointer' }}
                onClick={() => setTab('login')}>Sign in here</strong>.
            </p>
            <a href="https://t.me/Maxx24m" target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12, padding: '9px 0', background: 'rgba(33,150,243,.12)', border: '1px solid rgba(33,150,243,.3)', borderRadius: 'var(--radius)', color: '#2196f3', fontSize: 13, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#2196f3"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.48 14.479l-2.95-.924c-.641-.203-.654-.641.136-.953l11.527-4.448c.533-.194 1.002.131.37.094z"/></svg>
              Contact Admin on Telegram
            </a>
          </form>
        )}
      </div>
    </div>
  );
}
